import type {} from '@mui/x-date-pickers/themeAugmentation';
import { createTheme } from '@mui/material/styles';
import { red } from '@mui/material/colors';
import React from 'react';
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from 'react-router-dom';
import { LinkProps } from '@mui/material/Link';
import { buttonClasses } from '@mui/material/Button';
import {
  applyLanguageToPathname,
  readPreferredLanguage,
  SupportedLanguage,
} from '../routes/language';
// import { outlinedInputClasses } from '@mui/material/OutlinedInput';

const LinkBehavior = React.forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }
>((props, ref) => {
  const { href, ...other } = props;
  // Map href (MUI) -> to (react-router)
  return (
    <RouterLink
      data-testid="custom-link"
      ref={ref}
      to={applyLanguageToPathname(
        href.toString(),
        readPreferredLanguage() as SupportedLanguage
      )}
      {...other}
    />
  );
});

// A custom theme for this app
const dark_theme = createTheme({
  props: {
    // Name of the component ⚛️
    MuiButtonBase: {
      // The properties to apply
      disableRipple: true, // No more ripple, on the whole application 💣!
    },
  },
  typography: {
    fontFamily: 'Inter,sans-serif',
    fontSize: 14,
  },
  transitions: {
    /* duration: {
      shortest: 5,
      shorter: 5,
      short: 5,
      // most basic recommended timing
      standard: 5,
      // this is to be used in complex animations
      complex: 5,
      // recommended when something is entering screen
      enteringScreen: 5,
      // recommended when something is leaving screen
      leavingScreen: 5,
    }, */
    // create: () => 'none',
  },
  palette: {
    mode: 'dark',
    background: {
      default: '#111114',
      paper: '#1F1F22',
    },
    primary: {
      main: '#3B7BD9',
    },
    secondary: {
      main: '#FFFFFF',
    },
    error: {
      main: red.A400,
    },
    divider: '#bebeb633',
    borderGray: {
      main: '#bebeb633',
      light: '#bebeb633',
      dark: '#bebeb633',
    },
    navLink: '#C9CACC',
    description: {
      main: '#9a9a9a',
      light: '#9a9a9a',
    },
    // text: {
    //   primary: '#B0B8C4',
    // },
  },

  shape: {
    borderRadius: 18,
  },

  components: {
    MuiInput: {
      styleOverrides: {
        root: {
          '&:before': {
            borderBottom: '2px solid #bebeb633',
          },
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
            {
              display: 'none',
            },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
          '&:hover:not(.Mui-disabled, .Mui-error):before': {
            borderBottom: '2px solid #9a9a9a',
          },
          // '&.Mui-focused:after': {
          //   borderBottom: '2px solid var(--TextField-brandBorderFocusedColor)',
          // },
        },
      },
    },
    // MuiFilledInput: {
    //   styleOverrides: {
    //     root: {
    //       '&:before, &:after': {
    //         borderBottom: '2px solid var(--TextField-brandBorderColor)',
    //       },
    //       '&:hover:not(.Mui-disabled, .Mui-error):before': {
    //         borderBottom: '2px solid var(--TextField-brandBorderHoverColor)',
    //       },
    //       '&.Mui-focused:after': {
    //         borderBottom: '2px solid var(--TextField-brandBorderFocusedColor)',
    //       },
    //     },
    //   },
    // },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#bebeb633',
          // borderRadius: '18px',
        },
        root: {
          '&:before': {
            borderBottom: '2px solid #bebeb633',
          },
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
            {
              display: 'none',
            },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
          '&:hover:not(.Mui-disabled, .Mui-error):before': {
            borderBottom: '2px solid #9a9a9a',
          },
          [`&:hover .MuiOutlinedInput-notchedOutline`]: {
            borderColor: '#9a9a9a',
            transition: 'border-color 0.2s ease-in-out',
          },
          [`&.Mui-focused .MuiOutlinedInput-notchedOutline`]: {
            borderColor: '#3B7BD9',
            transition: 'border-color 0.2s ease-in-out',
          },
          // '&.Mui-focused:after': {
          //   borderBottom: '2px solid var(--TextField-brandBorderFocusedColor)',
          // },
        },
        // root: {
        //   [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
        //     borderColor: 'var(--TextField-brandBorderHoverColor)',
        //   },
        //   [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
        //     borderColor: 'var(--TextField-brandBorderFocusedColor)',
        //   },
        // },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          calcHeading: 'h1',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '30px',
          margin: '16px',
          width: 'calc(100% - 16px)',
          maxHeight: 'calc(100% - 16px)',
        },
      },
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      variants: [
        {
          props: { variant: 'default' },
          style: {
            backgroundColor: '#FFDF11',
            color: '#0E0F17',
            borderRadius: '18px',
            padding: '10px 32px',
            fontSize: '14px',
            fontWeight: '600',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#EDB110',
            },
            '&.Mui-disabled': {
              backgroundColor: '#0000001f',
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: '18px',
          // padding: '10px 32px',
          fontSize: '14px',
          fontWeight: '600',
          // textTransform: 'none',
        },
        contained: {
          backgroundColor: '#FFDF11',
          color: '#0E0F17',
          '&:hover': {
            backgroundColor: '#EDB110',
          },
          '&.Mui-disabled': {
            backgroundColor: '#0000001f',
          },
        },
      },
    },

    MuiButtonBase: {
      // styleOverrides: {
      //   root: {
      //     '&:hover': {
      //       backgroundColor: '#7c7c7b !important',
      //     },
      //   },
      // },
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          // transition: 'none !important',
          // animation: 'none !important',
        },
      },
    },

    // Name of the component
    MuiBottomNavigationAction: {
      styleOverrides: {
        // Name of the slot

        label: {
          fontSize: '9px',
          color: '#AAADB2',
          '&.Mui-selected': {
            fontSize: '9px',
            color: '#FF7348',
          },
        },
      },
    },

    MuiSnackbar: {
      styleOverrides: {
        root: {
          // bottom: '70px',
          // '@media (min-width: 600px)': {
          //   bottom: '64px',
          // },
        },
      },
    },

    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '16px',
          paddingTop: '16px',
          paddingRight: '24px',
          paddingLeft: '20px',
          paddingBottom: '16px',
          marginBottom: '20px',
          '@media (max-width: 899px)': {
            padding: '8px 0',
            marginBottom: '15px',
          },
          '&:before': {
            display: 'none',
          },
          ul: {
            paddingLeft: '20px',
          },
          ol: {
            paddingLeft: '20px',
          },
          '&:first-of-type': {
            borderTopRightRadius: '16px',
            borderTopLeftRadius: '16px',
            borderBottomRightRadius: '16px',
            borderBottomLeftRadius: '16px',
          },
          '&:last-of-type': {
            borderTopRightRadius: '16px',
            borderTopLeftRadius: '16px',
            borderBottomRightRadius: '16px',
            borderBottomLeftRadius: '16px',
            marginBottom: 0,
          },
        },
      },
    },
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          '& .MuiPickersLayout-actionBar': {
            // [`& .${buttonClasses.root}`]: {
            //   backgroundColor: 'red',
            // },
            [`& .${buttonClasses.text}`]: {
              color: '#FFDF11',
            },
            // [`& .${buttonClasses.root}:first-of-type`]: {
            //   color: 'red',
            // },
          },
        },
      },
    },
  },
});

dark_theme.typography.calcHeading = {
  display: 'flex',
  justifyContent: 'center',
  fontSize: '32px',
  fontWeight: '500',
  lineHeight: '1.2',
  margin: '32px 0',
  textAlign: 'center',
  [dark_theme.breakpoints.down('md')]: {
    justifyContent: 'flex-start',
    fontSize: '22px',
  },
};

export default dark_theme;
